import {
  applyMatchToNode,
  applyPartialCommitToPlan,
  expandBottomUp,
  listReadyProducts,
  planMatchStats,
  reclassifyNode,
  seedTopDown,
  splitComponentByStyle,
  updateNodeInPlan,
  withGeneratedSellableSkus,
  type ComposePlan,
} from "@/lib/manufacturing/composePlan";
import { executeComposeCommit } from "@/lib/manufacturing/composePlan/executeCommit";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { persistComposePlanRemote } from "./composePlanRemoteActions";

export function expandComposePlan(
  plan: ComposePlan,
  stylesRaw: string,
): { ok: true; plan: ComposePlan } | { ok: false; error: string } {
  const styles = stylesRaw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  let archetype = { ...plan.archetype, styles };
  const generated = withGeneratedSellableSkus(archetype, stylesRaw);
  if (generated.ok) archetype = generated.archetype;
  else if (plan.entryMode === "top_down" && !archetype.skuListRaw.trim()) {
    return { ok: false, error: generated.error };
  }
  const withArchetype = { ...plan, archetype };
  const next =
    withArchetype.entryMode === "bottom_up"
      ? expandBottomUp(withArchetype)
      : seedTopDown(withArchetype);
  if (next.nodes.length === 0) {
    return {
      ok: false,
      error:
        withArchetype.entryMode === "bottom_up"
          ? "Add at least one size and one style before expanding."
          : "Generate or paste at least one SKU line (CODE or CODE | Name).",
    };
  }
  return { ok: true, plan: { ...next, step: "review" } };
}

export function applyComposeMatch(
  plan: ComposePlan,
  nodeId: string,
  recipeId: string | null,
  theoretical: boolean,
  recipes: readonly MfgRecipeSummary[],
): ComposePlan {
  const node = plan.nodes.find((n) => n.id === nodeId);
  const recipe = recipeId ? recipes.find((r) => r.id === recipeId) : null;
  let boosts = plan.matchBoosts;
  let confirmedLinks = plan.confirmedLinks ?? [];
  if (recipe) {
    if (!boosts.includes(recipe.code)) {
      boosts = [...boosts, recipe.code, recipe.name];
    }
    if (node?.slotKey) {
      confirmedLinks = [
        ...confirmedLinks,
        {
          slotKey: node.slotKey,
          sizeLabel: node.sizeLabel,
          styleKey: node.styleKey,
          variantKey: node.variantKey,
          recipe_id: recipe.id,
          code: recipe.code,
        },
      ];
    }
  }
  return {
    ...plan,
    matchBoosts: boosts,
    confirmedLinks,
    nodes: updateNodeInPlan(plan.nodes, nodeId, (n) =>
      applyMatchToNode(n, recipeId, theoretical),
    ),
    updatedAt: new Date().toISOString(),
  };
}

export function applyComposeReclassify(
  plan: ComposePlan,
  nodeId: string,
  kind: MfgRecipeKind,
): ComposePlan {
  return {
    ...plan,
    nodes: updateNodeInPlan(plan.nodes, nodeId, (n) => reclassifyNode(n, kind)),
    updatedAt: new Date().toISOString(),
  };
}

export function applyComposeSplitByStyle(
  plan: ComposePlan,
  componentNodeId: string,
): { ok: true; plan: ComposePlan; message: string } | { ok: false; error: string } {
  const res = splitComponentByStyle(plan, componentNodeId);
  if (!res.ok) return res;
  return {
    ok: true,
    plan: res.plan,
    message: `Split into ${res.styles.length} style-specific parts (${res.styles.join(", ")}). Match each separately.`,
  };
}

export function applyComposeMatchMany(
  plan: ComposePlan,
  nodeIds: readonly string[],
  recipeId: string | null,
  theoretical: boolean,
  recipes: readonly MfgRecipeSummary[],
): ComposePlan {
  let next = plan;
  for (const id of nodeIds) {
    if (!next.nodes.some((n) => n.id === id)) continue;
    next = applyComposeMatch(next, id, recipeId, theoretical, recipes);
  }
  return next;
}

export function applyComposeSplitByStyleMany(
  plan: ComposePlan,
  componentNodeIds: readonly string[],
): { ok: true; plan: ComposePlan; message: string } | { ok: false; error: string } {
  let next = plan;
  let splitCount = 0;
  const errors: string[] = [];
  for (const id of componentNodeIds) {
    if (!next.nodes.some((n) => n.id === id && n.role === "component")) continue;
    const res = applyComposeSplitByStyle(next, id);
    if (!res.ok) {
      errors.push(res.error);
      continue;
    }
    next = res.plan;
    splitCount += 1;
  }
  if (splitCount === 0) {
    return {
      ok: false,
      error: errors[0] ?? "Nothing to split — select shared components used by multiple styles.",
    };
  }
  return {
    ok: true,
    plan: next,
    message:
      splitCount === 1
        ? `Split 1 component into style-specific parts.${errors.length ? ` (${errors.length} skipped)` : ""}`
        : `Split ${splitCount} components into style-specific parts.${errors.length ? ` (${errors.length} skipped)` : ""}`,
  };
}

export async function runComposePartialCommit(
  plan: ComposePlan,
  recipes: readonly MfgRecipeSummary[],
  selectedCommitIds: readonly string[],
): Promise<
  | {
      ok: true;
      message: string;
      plan: ComposePlan;
      result: Awaited<ReturnType<typeof executeComposeCommit>>;
      remaining: number;
      allDone: boolean;
      nextReadyIds: string[];
    }
  | { ok: false; message: string; result?: Awaited<ReturnType<typeof executeComposeCommit>> }
> {
  const ids =
    selectedCommitIds.length > 0
      ? [...selectedCommitIds]
      : listReadyProducts(plan).map((p) => p.id);
  if (ids.length === 0) {
    return {
      ok: false,
      message: "No ready products to commit. Finish matching slots on at least one SKU.",
    };
  }
  const result = await executeComposeCommit(plan, recipes, { productNodeIds: ids });
  if (result.committedProductNodeIds.length === 0 && result.failed.length) {
    return {
      ok: false,
      message: result.failed[0]?.error ?? "Commit failed",
      result,
    };
  }
  const parts = [
    `Committed ${result.committedProductNodeIds.length} product(s)`,
    `${result.createdStubs.length} stub(s)`,
    `${result.createdProducts.length} created`,
    `${result.linkedProducts.length} linked`,
  ];
  if (result.failed.length) parts.push(`${result.failed.length} failed`);
  const next = applyPartialCommitToPlan(plan, result, result.committedProductNodeIds);
  await persistComposePlanRemote(next);
  const stats = planMatchStats(next);
  return {
    ok: true,
    message: parts.join(" · "),
    plan: next,
    result,
    remaining: stats.pendingProducts + stats.readyProducts,
    allDone: stats.pendingProducts === 0 && stats.readyProducts === 0,
    nextReadyIds: listReadyProducts(next).map((p) => p.id),
  };
}
